import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Phone, Mail, MapPin, Send, Clock } from 'lucide-react';
import { SEO } from '../components/SEO';
import { Breadcrumb } from '../components/Breadcrumb';
import { BreadcrumbSchema } from '../components/SchemaOrg';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, formData);
      toast.success("Message envoyé !", {
        description: "Votre demande a bien été transmise. Nous vous recontacterons dans les plus brefs délais.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast.error("Erreur d'envoi", {
        description: "Une erreur est survenue. Appelez-nous directement au 09 70 70 58 63.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const breadcrumbItems = [
    { name: 'Contact', url: '/contact' }
  ];

  return (
    <>
      <SEO
        title="Contact et Devis Gratuit - Plombier Paris 17e | NHM Plomberie"
        description="Contactez NHM Plomberie pour devis gratuit ou urgence. Disponible 24/7. ☎ 09 70 70 58 63 · ✉ contact@nhm-plomberie.fr · Paris 17e"
        keywords="contact plombier Paris 17e, devis plomberie gratuit, numéro plombier urgence, email plombier 75017, rendez-vous plomberie"
        canonical="https://nhm-plomberie.fr/contact"
      />
      <BreadcrumbSchema items={[{ name: 'Accueil', url: 'https://nhm-plomberie.fr/' }, ...breadcrumbItems.map(item => ({ name: item.name, url: `https://nhm-plomberie.fr${item.url}` }))]} />

      <Breadcrumb items={breadcrumbItems} />

      {/* Hero Section */}
      <section className="bg-blue-900 text-white py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6">Contactez-Nous</h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Une question ? Un projet ? Une urgence ? Nous sommes là pour vous aider.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 -mt-20">
            <Card className="border-2 border-blue-600 shadow-xl">
              <CardContent className="pt-8 pb-8 text-center">
                <Phone className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Téléphone</h3>
                <a href="tel:0970705863" className="text-2xl font-bold text-blue-600 hover:text-blue-700">
                  09 70 70 58 63
                </a>
                <p className="text-sm text-gray-600 mt-2">Disponible 24/7</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-600 shadow-xl">
              <CardContent className="pt-8 pb-8 text-center">
                <Mail className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Email</h3>
                <a href="mailto:contact@nhm-plomberie.fr" className="text-lg text-blue-600 hover:text-blue-700 break-all">
                  contact@nhm-plomberie.fr
                </a>
                <p className="text-sm text-gray-600 mt-2">Réponse sous 24h</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-600 shadow-xl">
              <CardContent className="pt-8 pb-8 text-center">
                <MapPin className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Adresse</h3>
                <p className="text-gray-700">18 rue Legendre</p>
                <p className="text-gray-700">75017 Paris</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold mb-6 text-gray-900">Demande de Devis Gratuit</h2>
              <p className="text-gray-600 mb-8">
                Remplissez le formulaire ci-dessous et nous vous recontacterons dans les plus brefs délais 
                pour discuter de votre projet et vous fournir un devis gratuit et sans engagement.
              </p>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">Nom complet *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Votre nom"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="votre.email@exemple.com"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Téléphone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="06 12 34 56 78"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="subject">Sujet *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="Ex: Demande de devis pour rénovation salle de bain"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Décrivez votre projet ou votre problème..."
                    rows={6}
                    className="mt-2"
                  />
                </div>
                <Button type="submit" size="lg" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isSubmitting} data-testid="contact-form-submit-btn">
                  <Send className="mr-2" />
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                </Button>
              </form>
            </div>

            {/* Additional Info */}
            <div>
              <h2 className="text-3xl font-bold mb-6 text-gray-900">Informations Pratiques</h2>
              
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-6 h-6 text-blue-600" />
                    Horaires d'Ouverture
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="font-semibold">Lundi - Vendredi:</span>
                      <span>8h00 - 19h00</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Samedi:</span>
                      <span>9h00 - 17h00</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Dimanche:</span>
                      <span>Urgences uniquement</span>
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-blue-600 font-bold">Service d'urgence disponible 24h/24, 7j/7</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Zone d'Intervention</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">
                    Nous intervenons principalement à Paris 17e et dans les communes environnantes:
                  </p>
                  <ul className="space-y-1 text-gray-600">
                    <li>• Paris 17e</li>
                    <li>• Paris 8e, 9e, 16e, 18e</li>
                    <li>• Levallois-Perret</li>
                    <li>• Clichy</li>
                    <li>• Neuilly-sur-Seine</li>
                    <li>• Et autres arrondissements proches</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Pourquoi Nous Contacter ?</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">✓</span>
                      <span>Devis gratuit et sans engagement</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">✓</span>
                      <span>Conseils personnalisés d'experts</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">✓</span>
                      <span>Réponse rapide sous 24h</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">✓</span>
                      <span>Intervention d'urgence 24/7</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section className="py-16 bg-gray-50" data-testid="map-section">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4 text-gray-900 text-center">Où Nous Trouver</h2>
          <p className="text-gray-600 text-center mb-8">
            NHM Plomberie — 18 rue Legendre, 75017 Paris
          </p>
          <div className="max-w-5xl mx-auto rounded-xl overflow-hidden shadow-xl border-2 border-blue-600">
            <iframe
              src="https://www.google.com/maps?q=18+rue+Legendre+75017+Paris&output=embed"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localisation NHM Plomberie - 18 rue Legendre 75017 Paris"
              data-testid="google-map-iframe"
            ></iframe>
          </div>
          <div className="text-center mt-6">
            <Button asChild size="lg" variant="outline" data-testid="itineraire-btn">
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=18+rue+Legendre+75017+Paris"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <MapPin className="w-5 h-5" />
                Obtenir l'itinéraire
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Emergency CTA */}
      <section className="py-16 bg-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Urgence Plomberie ?</h2>
          <p className="text-xl mb-6">Ne perdez pas de temps, appelez-nous immédiatement !</p>
          <Button size="lg" className="bg-white text-red-600 hover:bg-red-50 text-xl px-8 py-6">
            <a href="tel:0970705863" className="flex items-center gap-2">
              <Phone className="w-6 h-6" />
              09 70 70 58 63
            </a>
          </Button>
        </div>
      </section>
    </>
  );
};

export default Contact;